import { ReactNode } from 'react';
import { NsColumn, NsImage } from '@britishgasenergy/react-nucleus';
import type { NsColumnProps } from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn/NsColumn';

import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsLandmark } from 'components/common/ns-landmark/NsLandmark';
import { NsTimeline } from 'components/common/ns-timeline/NsTimeline';
import { NsTimelineEvent } from 'components/common/ns-timeline/NsTimelineEvent';
import { MovingHomeOrderComplete } from './MovingHomeOrderComplete';
import { CrossSellOffer } from 'components/direct-sales/order-complete/components/CrossSellOffer';
import { CrossSellOfferCaveats } from 'components/direct-sales/order-complete/components/CrossSellOfferCaveats';

import { usePartner } from 'components/app/routes/usePartner';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { useHasSmartMeter } from 'components/direct-sales/quote/hooks/useHasSmartMeter';
import { useEvTariffInformation } from 'hooks/useEvTariffInformation';
import { useJaguar } from './hooks/useJaguar';
import { PARTNERS } from 'config/app-config';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { useMeterType } from 'hooks/useMeterType';

import jaguarStraightThumbUp from 'assets/images/jaguar-straight-thumb-up-1-1-crop.png';

export const testId = 'orderCompleteTestId';

type WrapperProps = {
  children: ReactNode;
};

const Wrapper = ({ children }: WrapperProps) => {
  return <>{children}</>;
};

export const OrderComplete = () => {
  const { isPrePay } = usePaymentMethod();
  const { isJaguar, isDesktop, columnRatio } = useJaguar();
  const { hasElectricitySmartMeter } = useHasSmartMeter();
  const { hasAnyTOUMeter, hasAnyPrepayMeter } = useMeterType();
  const { showEvTariffInformation } = useEvTariffInformation();
  const { activePartner } = usePartner();
  const isMoveInJourney = activePartner[PARTNERS.MOVE_IN];
  const isLoginEnabledJourney = useIsLoginEnabledJourney();
  const handleRestartJourney = () => {
    window.location.reload();
  };

  return (
    <>
      {isMoveInJourney && <MovingHomeOrderComplete />}
      {!isMoveInJourney && (
        <>
          <NsLandmark
            type="lakeside"
            heading={<>Thanks for joining British Gas</>}
          >
            <p>
              Here&apos;s what to expect. You&apos;ll get a confirmation email
              with this info in the next few minutes too. If you don&apos;t see
              it, please check your junk folder, and add{' '}
              <b>service@mail.energy.britishgas.co.uk</b> to your contacts.
            </p>
          </NsLandmark>
          <NsPanel testId={testId}>
            <NsColumn hockeypitch={columnRatio as NsColumnProps['hockeypitch']}>
              {isJaguar && !isDesktop && (
                <NsImage src={jaguarStraightThumbUp} />
              )}
              <NsTimeline type="standard">
                <NsTimelineEvent
                  wrapper={Wrapper}
                  status="completed"
                  heading="Order placed"
                >
                  <p>
                    We&apos;ll contact your current supplier to arrange
                    everything. It normally takes 3-5 days to switch suppliers
                  </p>
                </NsTimelineEvent>
                <NsTimelineEvent
                  wrapper={Wrapper}
                  status="inprogress"
                  heading="Check your inbox for our Welcome Pack!"
                >
                  <p>
                    Once final checks are complete, we will send you a welcome
                    email. This has all the details about your chosen tariff,
                    terms and conditions, cooling off period, start date, and
                    how to activate your online account. If it hasn&apos;t
                    arrived in the next 24 hours, just let us know.
                  </p>
                </NsTimelineEvent>

                {isPrePay && hasAnyTOUMeter && (
                  <NsTimelineEvent
                    wrapper={Wrapper}
                    status="pending"
                    heading="Topping up"
                    testId="prepay-tou"
                  >
                    <p>
                      We’ll be sending you a new smart card(s). Please keep
                      topping up as you do now until it arrives – we’ll let you
                      know when it’s on the way. Once we’re supplying your home,
                      we’ll also let you know how to top up online or through
                      our app.
                    </p>
                  </NsTimelineEvent>
                )}

                {isPrePay && hasAnyPrepayMeter && (
                  <NsTimelineEvent
                    wrapper={Wrapper}
                    status="pending"
                    heading="Topping up"
                    testId="prepay-traditional"
                  >
                    <p>
                      We’ll be sending you a new top up device. Please keep
                      topping up as you do now until it arrives – we’ll let you
                      know when it’s on the way.
                    </p>
                  </NsTimelineEvent>
                )}

                {!isPrePay && (
                  <NsTimelineEvent
                    wrapper={Wrapper}
                    status="pending"
                    heading="Your first meter reading"
                  >
                    <p>
                      If we need a meter reading from you, we&apos;ll get in
                      touch.
                    </p>
                  </NsTimelineEvent>
                )}

                {showEvTariffInformation && hasElectricitySmartMeter && (
                  <NsTimelineEvent
                    wrapper={Wrapper}
                    status="pending"
                    heading="Switch to our EV tariff"
                  >
                    <p>
                      Once we&apos;ve switched your supply we&apos;ll connect to
                      your smart meter. It can take about 2 weeks to get
                      everything set up. Then you&apos;ll be able to switch to
                      our EV tariff through your online account.
                    </p>
                  </NsTimelineEvent>
                )}
                {showEvTariffInformation && !hasElectricitySmartMeter && (
                  <>
                    <NsTimelineEvent
                      wrapper={Wrapper}
                      status="pending"
                      heading="Book your smart meter"
                    >
                      <p>
                        Once we&apos;ve switched your supply, we&apos;ll get all
                        the details about your meter and update your account -
                        this can take about 2 weeks. Then you&apos;ll be able to
                        book a smart meter appointment through your online
                        account, at no extra cost.
                      </p>
                    </NsTimelineEvent>
                    <NsTimelineEvent
                      wrapper={Wrapper}
                      status="pending"
                      heading="Switch to our EV tariff"
                    >
                      <p>
                        After your smart meter is fitted, we&apos;ll connect it
                        to our system. Then you can switch to our EV tariff
                        through your online account.
                      </p>
                    </NsTimelineEvent>
                  </>
                )}
              </NsTimeline>
              {isJaguar && isDesktop && <NsImage src={jaguarStraightThumbUp} />}
            </NsColumn>
            {isLoginEnabledJourney && (
              <NsPrimaryCta onClick={handleRestartJourney}>
                Start new sale
              </NsPrimaryCta>
            )}
          </NsPanel>
          <CrossSellOffer />
          <CrossSellOfferCaveats />
        </>
      )}
    </>
  );
};

export default OrderComplete;
